import { Route, Routes, Navigate } from 'react-router-dom';
import React from 'react';
import { useSelector } from 'react-redux';
import { isCurrentUserStaffOrSuperAdmin, isCurrentUserSuperUser } from '../common/selectors/auth';
import OrganizationManagement from '../areas/settings/organization/components/OrganizationManagement';
import OrganizationWizard from '../areas/settings/organization/components/OrganizationWizard';
import AccountManagement from '../areas/settings/account-management/components/AccountManagement';
import SystemHealth from '../areas/settings/system-health/components/SystemHealth';
import SentryTesting from '../areas/settings/sentry-testing';
import MessagingMonitoring from '../areas/settings/messaging-monitoring/components/MessagingMonitoring';

import { FeatureFlagList } from '../areas/settings/feature-flags/components/FeatureFlagList';
import SystemSetup from '../areas/settings/system-setup/components/SystemSetup';
import useFeatures from '../common/hooks/useFeatures';
import { RepositoryManagement } from '../areas/settings/repository-management/components/RepositoryManagement';
import { FileUpload } from '../areas/settings/repository-management/components/FileUpload';
import { RepositoryFileDetails } from '../areas/settings/repository-management/components/RepositoryFileDetails';
import OrgGoals from '../areas/settings/recommendation-templates/org-goals';
import TeamRecs from '../areas/settings/recommendation-templates/team-recs';
import Foundations from 'areas/settings/recommendation-templates/foundations';
import Interventions from 'areas/settings/recommendation-templates/interventions';
import CategoryManagement from '../areas/settings/category/components/CategoryManagement';
import MemberEdit from '../areas/settings/employee-management/components/MemberEdit';
import OrganizationEdit from 'areas/settings/organization/components/OrganizationEdit';
import CategoryEdit from 'areas/settings/category/components/CategoryEdit';
import SurveyQuestionManagement from 'areas/settings/survey-questions/components/SurveyQuestionManagement';
import SurveyQuestionEdit from 'areas/settings/survey-questions/components/SurveyQuestionEdit';
import PulseSurveyManagement from '@/areas/settings/pulse-surveys/components/QuestionSetManagement';
import PulseSurveyEdit from '../areas/settings/pulse-surveys/components/QuestionSetEdit';
import { PerformanceMetricsManager } from '../areas/settings/performance-metrics/components/PerformanceMetricsManager';
import ProvenanceQueuePage from '../areas/knowledge-repository/provenance/components/ProvenanceQueuePage';
import ActivityLog from '../areas/settings/activity-log/components/ActivityLog';

export const AdminRouter = () => {
  const { initialized: featuresInitialized, isFeatureEnabled } = useFeatures();
  const isStaffOrSuperUser = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const isKnowledgeIngestionEnabled = isFeatureEnabled('Knowledge Repository Ingestion Pipeline');

  // Don't block on features - they're only needed for specific feature-gated routes
  // If features aren't loaded yet, feature-gated routes just won't be accessible

  // Block all non-staff/non-superuser from accessing ANY admin routes
  if (!isStaffOrSuperUser) {
    return <Navigate to="/app/access-denied" replace />;
  }

  return (
    <Routes>
      <Route path={'/'} element={<OrganizationManagement />} />
      <Route path={''} element={<OrganizationManagement />} />
      <Route path={'organization'} element={<OrganizationManagement />} />
      <Route path={'organization/create'} element={<OrganizationWizard />} />
      <Route path={'organization/:id/edit'} element={<OrganizationEdit />} />
      <Route path={'organization/:memberId'} element={<MemberEdit />} />
      <Route path={'categories'} element={<CategoryManagement />} />
      <Route path={'categories/create'} element={<CategoryEdit />} />
      <Route path={'categories/:id/edit'} element={<CategoryEdit />} />

      <Route path={'survey-questions'} element={<SurveyQuestionManagement />} />
      <Route path={'survey-questions/create'} element={<SurveyQuestionEdit />} />
      <Route path={'survey-questions/:id/edit'} element={<SurveyQuestionEdit />} />

      <Route path={'pulse-surveys'} element={<PulseSurveyManagement />} />
      <Route path={'pulse-surveys/:id/edit'} element={<PulseSurveyEdit />} />

      <Route path={'accounts'} element={<AccountManagement />} />
      <Route path={'system-health'} element={<SystemHealth />} />
      <Route path={'system-health/:tab'} element={<SystemHealth />} />
      <Route path={'jobs'} element={<Navigate to="/settings/admin/system-health/jobs" replace />} />
      <Route path={'sentry-testing'} element={isSuperUser ? <SentryTesting /> : <Navigate to="/app/access-denied" replace />} />
      <Route path={'messaging-monitoring'} element={isSuperUser ? <MessagingMonitoring /> : <Navigate to="/app/access-denied" replace />} />
      <Route path={'activity-log'} element={isSuperUser ? <ActivityLog /> : <Navigate to="/app/access-denied" replace />} />

      <Route path={'system-setup'} element={isSuperUser ? <SystemSetup /> : <Navigate to="/app/access-denied" replace />} />
      <Route path={'system-setup/:tab'} element={isSuperUser ? <SystemSetup /> : <Navigate to="/app/access-denied" replace />} />
      <Route path={'features'} element={<Navigate to="/settings/admin/system-setup/features" replace />} />
      <Route path={'ai-settings'} element={<Navigate to="/settings/admin/system-setup/ai-settings" replace />} />
      <Route path={'repository-management'} element={<RepositoryManagement />} />
      <Route path={'repository-management/add-file'} element={<FileUpload />} />
      <Route path={'repository-management/file-details'} element={<RepositoryFileDetails />} />
      <Route path={'recommendation-templates/foundations/*'} element={<Foundations />} />
      <Route path={'recommendation-templates/interventions/*'} element={<Interventions />} />
      <Route path={'recommendation-templates/org-goals/*'} element={<OrgGoals />} />
      <Route path={'recommendation-templates/team-recs/*'} element={<TeamRecs />} />
      <Route
        path={'provenance-queue'}
        element={
          isKnowledgeIngestionEnabled
            ? <ProvenanceQueuePage />
            : <Navigate to="/app/access-denied" replace />
        }
      />
    </Routes>
  );
};
